import type { HTMLAttributes } from 'react';
import {
  CircleAlert,
  CircleCheck,
  Sparkles,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { LineSegment, RunIcon } from '@/lib/emailStatusRun';

export interface ToastStatusAction {
  label: string;
  onClick: () => void;
}

export interface ToastStatusProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> {
  icon: RunIcon;
  headline: string;
  /** Second line as segments; empty when there is no line (no sender name). */
  secondLine: LineSegment[];
  /** Text buttons under the message, in Figma order. */
  actions?: ToastStatusAction[];
  /** Renders the ✕ when provided. */
  onClose?: () => void;
  closeLabel?: string;
}

/** "Icon colour carries the state; the words carry the meaning." */
const ICONS: Record<RunIcon, { Icon: LucideIcon; colorClass: string }> = {
  sparkles: { Icon: Sparkles, colorClass: 'text-icon-default' },
  'sparkles-warning': { Icon: Sparkles, colorClass: 'text-warning' },
  'circle-check': { Icon: CircleCheck, colorClass: 'text-success' },
  'triangle-alert': { Icon: TriangleAlert, colorClass: 'text-warning' },
  'circle-alert': { Icon: CircleAlert, colorClass: 'text-icon-error' },
};

/**
 * `Toast / Status` (Figma 10976:6373) — the bottom toast that narrates Yaruta
 * reading emails (YAR-477). Same card as `Toast / Action`, laid out as
 * `.Alerts Width=default`: a two-line message with the actions underneath.
 *
 * Presentational only. What it says comes from `computeStatusToast`; the
 * placement, visible-time clock, motion and polite announcement belong to
 * `BottomToastSlot`. The card itself is never tappable — only its buttons.
 *
 * Tap areas: the text buttons are 28px tall and ✕ 24px as drawn; invisible
 * `before:` boxes extend each to 44px without changing how they look.
 */
export function ToastStatus({
  icon,
  headline,
  secondLine,
  actions = [],
  onClose,
  closeLabel = 'Close',
  className = '',
  ...rest
}: ToastStatusProps) {
  const { Icon, colorClass } = ICONS[icon];
  return (
    <div
      className={[
        // Figma: surface/default fill, 1px border/neutral/default-subtle,
        // 8px radius, 12px padding + gap, "md - popover" shadow.
        'flex w-full items-start gap-3 rounded-md border border-border bg-surface p-3',
        'shadow-[0px_0px_8px_-2px_rgba(56,64,74,0.08)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <Icon
        size={24}
        strokeWidth={1.5}
        aria-hidden="true"
        className={['shrink-0', colorClass].join(' ')}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* body-md/medium, one line, ellipsis. */}
        <p className="m-0 truncate text-base font-medium text-muted-foreground">
          {headline}
        </p>
        {secondLine.length > 0 && (
          // body-sm/regular.
          <p className="m-0 truncate text-sm font-normal text-muted-foreground">
            {secondLine.map((segment, i) =>
              segment.tone === 'error' ? (
                <span
                  key={i}
                  className="text-text-error"
                >
                  {segment.text}
                </span>
              ) : (
                <span key={i}>{segment.text}</span>
              ),
            )}
          </p>
        )}
        {actions.length > 0 && (
          <div className="flex items-start gap-3 pt-1.5">
            {actions.map((action) => (
              <Button
                key={action.label}
                type="button"
                variant="tertiary"
                size="sm"
                onClick={action.onClick}
                className="relative shrink-0 before:absolute before:inset-x-0 before:-inset-y-2"
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="relative inline-flex size-6 shrink-0 cursor-pointer items-center justify-center text-muted-foreground before:absolute before:-inset-2.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <X
            size={24}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}
